import { useEffect, useRef, RefObject } from "react";
import { useLocation } from "react-router-dom";

interface UseScrollToMessageProps {
  messages: any[];
  containerRef: RefObject<HTMLDivElement | null>;
  loading: boolean;
  commentsLoading?: boolean;
  messageIdPrefix?: string;
  scrollOffset?: number;
  highlightColor?: string;
  highlightDuration?: number;
}

/**
 * Custom hook to scroll to a specific message when returning from a thread page
 * @param messages - Array of messages to search through
 * @param containerRef - Ref to the scrollable container
 * @param loading - Whether messages are currently loading
 * @param commentsLoading - Whether comments are currently loading (optional)
 * @param messageIdPrefix - Prefix for message element IDs (default: "message-")
 * @param scrollOffset - Offset from top when scrolling (default: 20)
 * @param highlightColor - Color to highlight the message (default: "#f0f9ff")
 * @param highlightDuration - Duration to highlight in ms (default: 2000)
 */
export default function useScrollToMessage({
  messages,
  containerRef,
  loading,
  commentsLoading = false,
  messageIdPrefix = "message-",
  scrollOffset = 20,
  highlightColor = "#f0f9ff",
  highlightDuration = 2000,
}: UseScrollToMessageProps) {
  const location = useLocation();
  const hasScrolledToMessageRef = useRef<string | null>(null);

  useEffect(() => {
    const scrollToMessageId = location.state?.scrollToMessageId;

    // Reset scroll ref if messageId changes
    if (scrollToMessageId && hasScrolledToMessageRef.current !== scrollToMessageId) {
      hasScrolledToMessageRef.current = null;
    }

    if (
      scrollToMessageId &&
      messages.length > 0 &&
      containerRef.current &&
      !loading &&
      !commentsLoading &&
      hasScrolledToMessageRef.current !== scrollToMessageId
    ) {
      // Wait for DOM to update and messages to be fully rendered
      const scrollToMessage = () => {
        const messageElement = document.getElementById(`${messageIdPrefix}${scrollToMessageId}`);
        const container = containerRef.current;

        if (messageElement && container) {
          // Check if element is actually in the DOM and visible
          if (!messageElement.offsetParent && messageElement.style.display === "none") {
            return; // Element not visible, skip
          }

          // Get the container's scroll position
          const containerRect = container.getBoundingClientRect();
          const elementRect = messageElement.getBoundingClientRect();

          // Calculate the scroll position needed
          // elementRect.top is relative to viewport, containerRect.top is container's position in viewport
          // We need: current scroll + (element position in container)
          const elementTopInContainer =
            elementRect.top - containerRect.top + container.scrollTop;
          const scrollPosition = elementTopInContainer - scrollOffset;

          container.scrollTo({
            top: Math.max(0, scrollPosition),
            behavior: "smooth",
          });

          // Mark as scrolled to avoid multiple attempts
          hasScrolledToMessageRef.current = scrollToMessageId;

          // Highlight the message briefly
          const originalBg = messageElement.style.backgroundColor;
          messageElement.style.transition = "background-color 0.3s ease";
          messageElement.style.backgroundColor = highlightColor;
          setTimeout(() => {
            if (messageElement) {
              messageElement.style.backgroundColor = originalBg || "";
            }
          }, highlightDuration);
        }
      };

      // Use requestAnimationFrame to ensure DOM is ready, then try scrolling
      requestAnimationFrame(() => {
        setTimeout(scrollToMessage, 100);
        // Retry after a longer delay in case messages are still rendering
        setTimeout(scrollToMessage, 500);
      });
    }
  }, [messages, location.state, loading, commentsLoading, containerRef, messageIdPrefix, scrollOffset, highlightColor, highlightDuration]);
}

